<template>
  <!-- Mirrors the live-stream session row design (LiveStream.vue
       `ls-session-row`): time column + body (title / subtitle / speakers),
       with the register/unregister control on the right.
       Clicking anywhere on the row opens the detail popup. The row is deliberately not
       given button semantics, since it already contains buttons and nesting them reads badly
       for screen readers; the explicit View details control below is the keyboard path. -->
  <article
    class="os-row"
    :class="{
      'os-row--registered': session.isRegistered && !isCancelled,
      'os-row--cancelled': isCancelled
    }"
    @click="openDetail"
  >
    <div class="os-row__time">
      <span class="os-row__date">{{ formatDay(session.startAt) }}</span>
      <span class="os-row__time-from">{{ formatTime(session.startAt) }}</span>
      <span class="os-row__time-to">{{ formatTime(session.endAt) }}</span>
    </div>

    <div class="os-row__body">
      <div class="os-row__title">{{ session.title }}</div>
      <div v-if="session.sponsorName" class="os-row__subtitle">
        {{ session.sponsorName }}
      </div>
      <div v-if="speakerSummary" class="os-row__meta">
        <i class="fas fa-microphone"></i>
        {{ speakerSummary }}
      </div>
      <div v-if="session.location" class="os-row__meta">
        <i class="fas fa-map-marker-alt"></i>
        {{ session.location }}
      </div>
      <div v-if="session.capacity" class="os-row__meta">
        <i class="fas fa-users"></i>
        {{ session.registeredCount }} / {{ session.capacity }}
      </div>
      <button type="button" class="os-row__details" @click.stop="openDetail">
        <span>{{ $t('Views.OpenSessions.viewDetails') }}</span>
        <i class="fal fa-arrow-right" aria-hidden="true"></i>
      </button>
    </div>

    <button
      type="button"
      class="os-row__cta"
      :class="ctaStateClass"
      :disabled="cta.disabled"
      :aria-busy="pending"
      :title="ctaLabel"
      :aria-label="ctaLabel"
      @click.stop="onCtaClick"
    >
      <i v-if="pending" class="fal fa-spinner-third animate-spin"></i>
      <i v-else-if="isCancelled" class="fal fa-ban"></i>
      <i v-else-if="session.isRegistered" class="fal fa-check"></i>
      <i v-else-if="session.isFull" class="fal fa-users-slash"></i>
      <i v-else class="fal fa-plus"></i>
    </button>
  </article>
</template>

<script>
import { formatEventDateTime } from '@/helpers/event-time';
import { openSessionCtaState, CTA_VARIANT } from '@/helpers/open-session-cta';
import { englishOnlyI18nMixin } from '@/mixins';

export default {
  name: 'OpenSessionCard',
  mixins: [englishOnlyI18nMixin],
  props: {
    session: { type: Object, required: true },
    pending: { type: Boolean, default: false }
  },
  computed: {
    eventTz() {
      return this.$store.getters['CurrentEvent/getTimezone'];
    },
    eventMode() {
      return this.$store.getters['CurrentEvent/getTimezoneMode'];
    },
    isCancelled() {
      return this.session.status === 'Cancelled';
    },
    speakerSummary() {
      const ids = this.session.speakerIds || [];
      if (!ids.length) return '';
      return this.$tc('Views.OpenSessions.speakerCount', ids.length, {
        count: ids.length
      });
    },
    // Shared with OpenSessionDetailModal so the row and the popup can never disagree
    // about whether this session can still be joined.
    cta() {
      return openSessionCtaState(this.session, this.pending);
    },
    ctaLabel() {
      return this.$t(this.cta.labelKey);
    },
    ctaStateClass() {
      if (this.cta.variant === CTA_VARIANT.DISABLED)
        return 'os-row__cta--disabled';
      if (this.cta.variant === CTA_VARIANT.REGISTERED)
        return 'os-row__cta--registered';
      return 'os-row__cta--register';
    }
  },
  methods: {
    formatDay(iso) {
      return formatEventDateTime(iso, this.eventTz, this.eventMode, {
        month: 'short',
        day: 'numeric'
      });
    },
    formatTime(iso) {
      return (
        formatEventDateTime(iso, this.eventTz, this.eventMode, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }) || ''
      );
    },
    onCtaClick() {
      if (this.cta.disabled) return;
      this.$emit(this.cta.action, this.session);
    },
    openDetail() {
      this.$emit('open', this.session);
    }
  }
};
</script>

<style scoped>
/* Matches LiveStream.vue `.ls-session-row` exactly */
.os-row {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.os-row:hover {
  border-color: var(--color-primary, #047873);
  box-shadow: 0 2px 6px rgb(var(--brand-ramp-600) / 0.08);
}
.os-row--registered {
  border-color: var(--color-primary, #047873);
  background: rgb(var(--brand-ramp-50));
}
.os-row--cancelled {
  opacity: 0.7;
}

.os-row__time {
  flex-shrink: 0;
  width: 64px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}
.os-row__date {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-primary, #047873);
  background: rgb(var(--brand-ramp-50));
  padding: 2px 6px;
  border-radius: 6px;
  line-height: 1.1;
  margin-bottom: 2px;
}
.os-row__time-from {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  line-height: 1.1;
}
.os-row__time-to {
  font-size: 12px;
  color: #6b7280;
}

.os-row__body {
  flex: 1;
  min-width: 0;
}
.os-row__title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
  margin-bottom: 2px;
}
.os-row__subtitle {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.3;
  margin-bottom: 4px;
}
.os-row__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.3;
}
.os-row__meta + .os-row__meta {
  margin-top: 2px;
}
.os-row__meta i {
  color: var(--color-primary, #047873);
  font-size: 12px;
  width: 14px;
  text-align: center;
}

.os-row__details {
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  background: none;
  border: 0;
  font-size: 12px;
  font-weight: 700;
  color: var(--color-primary, #047873);
  cursor: pointer;
}
.os-row__details:hover {
  text-decoration: underline;
}
.os-row__details:focus-visible {
  outline: 2px solid var(--color-primary, #047873);
  outline-offset: 2px;
  border-radius: 4px;
}
.os-row__details i {
  font-size: 10px;
}

/* Register / unregister control — sits where the live-dot does */
.os-row__cta {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid var(--color-primary, #047873);
  background: #ffffff;
  color: var(--color-primary, #047873);
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.os-row__cta--register:hover {
  background: var(--color-primary, #047873);
  color: #ffffff;
}
.os-row__cta--registered {
  background: var(--color-primary, #047873);
  border-color: var(--color-primary, #047873);
  color: #ffffff;
}
.os-row__cta--disabled {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}
</style>
